import axios, { CanceledError } from "axios";
import { toast } from "react-toastify";

import { accessTokenSecondsRemaining } from "../auth/adoptKeycloakSession";
import { redirectToMarketplaceLogin } from "../auth/sessionExpiry";
import keycloak from "../config/keycloak.jsx";
import {
  API_VERSION,
  ASSET_URL,
  BACK_END_URL,
  DPP_REGISTRY_URL,
} from "./constants.jsx";
import { removeVersionAttributesFromJsonPayload } from "./sanitizePayload";

const UNAUTHORIZED_MESSAGE =
  "You are not authorized to perform this action. Please login.";
const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL?.replace(/\/$/, "");
const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM;

function normalizeApiError(error) {
  const status = error.response?.status;
  const body = error.response?.data;
  const responseError = body?.error ?? body;
  const code = responseError?.code ?? String(status ?? "API_ERROR");
  const problemDetail =
    typeof body?.detail === "string" ? body.detail.trim() : "";
  const message =
    responseError?.message ??
    (problemDetail || error.message || "Request failed");
  const details = Array.isArray(responseError?.details)
    ? responseError.details
    : undefined;

  return {
    code: String(code),
    message: String(message),
    details,
    status,
  };
}

function shouldAttachAuthorization(config) {
  return config.skipAuth !== true && Boolean(keycloak?.token);
}

function isFormDataPayload(data) {
  return typeof FormData !== "undefined" && data instanceof FormData;
}

async function refreshTokenIfPossible() {
  if (!keycloak?.token) return;
  if (typeof keycloak?.updateToken !== "function") return;
  if (accessTokenSecondsRemaining(keycloak) > 60) return;
  try {
    await keycloak.updateToken(60);
  } catch {
    redirectToMarketplaceLogin();
    throw new Error("Session expired");
  }
}

function canRefreshToken() {
  return Boolean(keycloak?.token && keycloak?.updateToken);
}

function attachAuthorizationHeader(config) {
  if (!shouldAttachAuthorization(config)) return config;

  config.headers = config.headers ?? {};
  config.headers.Authorization = `Bearer ${keycloak.token}`;
  return config;
}

function removeContentTypeForFormData(config) {
  if (!isFormDataPayload(config.data)) return config;

  config.headers = config.headers ?? {};
  if (typeof config.headers.delete === "function") {
    config.headers.delete("Content-Type");
    config.headers.delete("content-type");
  } else {
    delete config.headers["Content-Type"];
    delete config.headers["content-type"];
  }
  return config;
}

function createBackendClient(baseURL) {
  return axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
}

function configureBackendClient(client) {
  client.interceptors.request.use(async (config) => {
    config.data = removeVersionAttributesFromJsonPayload(config.data);
    removeContentTypeForFormData(config);
    await refreshTokenIfPossible();
    return attachAuthorizationHeader(config);
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error instanceof CanceledError) {
        return Promise.reject(error);
      }

      error.normalizedApiError = normalizeApiError(error);

      const originalRequest = error.config;
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        canRefreshToken()
      ) {
        originalRequest._retry = true;

        try {
          await refreshTokenIfPossible();
          attachAuthorizationHeader(originalRequest);
          return client(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }

      if (
        error.response?.status === 401 &&
        !originalRequest?.skipUnauthorizedToast
      ) {
        toast.error(UNAUTHORIZED_MESSAGE);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export const apiClient = configureBackendClient(
  createBackendClient(`${BACK_END_URL}/${API_VERSION}`)
);

export const backendClient = configureBackendClient(
  createBackendClient(BACK_END_URL)
);

export const assetClient = axios.create({
  baseURL: ASSET_URL,
});

/** Unauthenticated DPP registry client. Never attach Keycloak tokens to this origin. */
export const dppRegistryClient = axios.create({
  baseURL: DPP_REGISTRY_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const keycloakUserInfoClient = axios.create({
  baseURL:
    keycloakUrl && keycloakRealm
      ? `${keycloakUrl}/realms/${keycloakRealm}/protocol/openid-connect`
      : undefined,
});

export function getResponseData(response) {
  return response.data;
}

export { CanceledError, normalizeApiError };
